import type {
  CmmdProviderAccountInventoryAccount,
  CmmdProviderUsageModelWindow,
  CmmdProviderUsageSnapshot,
  CmmdProviderUsageWindow,
} from "@cmmd-forge/contracts";
import { AlertTriangleIcon } from "lucide-react";

import { useProviderUsageRefresh } from "../../usage/useProviderUsage";
import { PROVIDER_SETTINGS_ICON_BY_PROVIDER } from "../providerSettingsIcons";
import { formatProviderUsageRefreshFailure } from "./providerUsageRefreshFailureCopy";
import { formatProviderUsageRelativeTime } from "./providerUsageRelativeTime";
import { formatProviderUsageResetLabel } from "./providerUsageResetLabel";
import { UsageCard, UsageCardRefreshButton, UsageMeterBar } from "./usageCard";

const PROVIDER_LABEL: Record<"codex" | "claude", string> = {
  codex: "Codex",
  claude: "Claude",
};

export function ProviderUsageAccountCard({
  account,
  snapshot,
}: {
  readonly account: CmmdProviderAccountInventoryAccount;
  readonly snapshot: CmmdProviderUsageSnapshot | null;
}) {
  const provider = account.provider === "claudeAgent" ? "claude" : "codex";
  const { refresh, isRefreshing, refreshFailure } = useProviderUsageRefresh(
    provider,
    account.providerAccountId,
  );
  const providerLabel = PROVIDER_LABEL[provider];
  const ProviderIcon = PROVIDER_SETTINGS_ICON_BY_PROVIDER[account.provider];
  const isConnected = !account.needsReconnect;

  return (
    <UsageCard
      action={
        <UsageCardRefreshButton
          isDisabled={!isConnected}
          isRefreshing={isRefreshing}
          label={`Refresh ${providerLabel} usage`}
          onRefresh={refresh}
          tooltip={
            !isConnected
              ? `Connect ${providerLabel} to refresh usage`
              : isRefreshing
                ? "Refreshing"
                : `Refresh ${providerLabel} usage`
          }
        />
      }
      icon={
        ProviderIcon ? (
          <span data-provider-icon={account.provider}>
            <ProviderIcon aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
          </span>
        ) : null
      }
      kind={`provider-account:${account.provider}`}
      subtitle={formatProviderAccountSubtitle(account, snapshot)}
      title={`${providerLabel}${account.isDefault ? " · Default" : ""}`}
    >
      <ProviderUsageCardBody
        isConnected={isConnected}
        providerLabel={providerLabel}
        snapshot={snapshot}
      />
      {refreshFailure ? (
        <p
          className="text-warning-foreground text-xs"
          data-refresh-failure-code={refreshFailure.code ?? "unknown"}
          role="status"
        >
          {formatProviderUsageRefreshFailure(refreshFailure.code)}
        </p>
      ) : null}
    </UsageCard>
  );
}

function formatProviderAccountSubtitle(
  account: CmmdProviderAccountInventoryAccount,
  snapshot: CmmdProviderUsageSnapshot | null,
): string {
  const accountLabel = firstAvailableLabel([
    account.accountDisplayName,
    account.accountEmail,
    snapshot?.accountDisplayName,
    snapshot?.accountEmail,
  ]);
  const planType = account.planType ?? snapshot?.planType;
  const identity = planType ? `${accountLabel} · ${planType}` : accountLabel;
  return account.needsReconnect ? `${identity} · Reconnect required` : identity;
}

function firstAvailableLabel(values: ReadonlyArray<string | null | undefined>): string {
  return values.find((value): value is string => Boolean(value)) ?? "Connected account";
}

/**
 * CMMD's schema does not promise unique model names in `modelWindows`, so the
 * first window reported for a given model wins and later duplicates are
 * dropped — keeping React's `key` a real per-model identity instead of
 * falling back to array index, which would render two indistinguishable rows
 * rather than surface the duplication.
 */
function dedupeModelWindowsByModel(
  modelWindows: ReadonlyArray<CmmdProviderUsageModelWindow>,
): ReadonlyArray<CmmdProviderUsageModelWindow> {
  const seenModels = new Set<string>();
  return modelWindows.filter((modelWindow) => {
    if (seenModels.has(modelWindow.model)) return false;
    seenModels.add(modelWindow.model);
    return true;
  });
}

function ProviderUsageCardBody({
  providerLabel,
  snapshot,
  isConnected,
}: {
  readonly providerLabel: string;
  readonly snapshot: CmmdProviderUsageSnapshot | null;
  readonly isConnected: boolean;
}) {
  if (snapshot === null) {
    return (
      <p className="text-muted-foreground text-sm">
        {isConnected
          ? `Connected, waiting for the first ${providerLabel} usage observation.`
          : `${providerLabel} account needs to reconnect.`}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {snapshot.provider === "claude" ? (
        <ProviderUsageWindowRow label="5-hour" window={snapshot.fiveHour} />
      ) : null}
      <ProviderUsageWindowRow label="7-day" window={snapshot.sevenDay} />
      {/* Older CMMD responses predate this field; a missing array is "none", not a crash.
          Deduped by model first: CMMD's own schema does not promise unique model
          names, and keying by array index instead would render two indistinguishable
          rows for the same model rather than surfacing the data problem. */}
      {dedupeModelWindowsByModel(snapshot.modelWindows ?? []).map((modelWindow) => (
        <ProviderUsageWindowRow
          key={modelWindow.model}
          label={`7-day · ${modelWindow.model}`}
          window={modelWindow}
        />
      ))}
      <ProviderUsageCreditsRow provider={snapshot.provider} credits={snapshot.credits} />
      <span className="text-muted-foreground text-xs">{formatProviderUsageSource(snapshot)}</span>
    </div>
  );
}

/**
 * Codex pay-as-you-go balance (FOR-32770). Claude has no credits concept, and
 * a null balance means "never observed", which must read differently from a
 * real zero. Hiding the row keeps "absent" honest instead of implying empty.
 */
function ProviderUsageCreditsRow({
  provider,
  credits,
}: {
  readonly provider: "codex" | "claude";
  readonly credits: number | null;
}) {
  if (provider !== "codex" || credits === null) return null;
  const isDepleted = credits === 0;
  return (
    <div className="flex flex-col gap-1.5" data-credits-row={isDepleted ? "depleted" : "ok"}>
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-foreground text-sm">Credits</span>
        <span
          className={`flex items-center gap-1 text-sm tabular-nums ${
            isDepleted ? "text-warning-foreground" : "text-foreground"
          }`}
        >
          {isDepleted ? (
            <AlertTriangleIcon aria-hidden="true" className="size-3.5 text-warning-foreground" />
          ) : null}
          {`Credits: ${credits.toLocaleString("en-US")} left`}
        </span>
      </div>
    </div>
  );
}

function formatProviderUsageSource(snapshot: CmmdProviderUsageSnapshot): string {
  const sourceLabel = snapshot.source === "active_poll" ? "Live" : "Last observed";
  const staleLabel = snapshot.stale ? ". May be out of date." : "";
  return `${sourceLabel} ${formatProviderUsageRelativeTime(snapshot.observedAt)}${staleLabel}`;
}

function ProviderUsageWindowRow({
  label,
  window,
}: {
  readonly label: string;
  readonly window: CmmdProviderUsageWindow | null;
}) {
  if (window === null) {
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-foreground text-sm">{label}</span>
        <span className="text-muted-foreground text-xs">No data yet.</span>
      </div>
    );
  }

  const share = Math.min(1, Math.max(0, window.usedPercent / 100));
  const isWarning = window.status !== "allowed";
  const fillClassName =
    window.status === "rejected"
      ? "bg-destructive"
      : window.status === "allowed_warning"
        ? "bg-warning"
        : "bg-primary";

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-foreground text-sm">{label}</span>
        <span className="flex items-center gap-1 text-foreground text-sm tabular-nums">
          {isWarning ? (
            <AlertTriangleIcon aria-hidden="true" className="size-3.5 text-warning-foreground" />
          ) : null}
          {window.usedPercent}% used
        </span>
      </div>
      <UsageMeterBar fillClassName={fillClassName} share={share} />
      <span className="text-muted-foreground text-sm md:text-xs">
        <time dateTime={window.resetsAt}>{formatProviderUsageResetLabel(window.resetsAt)}</time> ·{" "}
        {100 - window.usedPercent}% remaining
      </span>
    </div>
  );
}
